import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  Box,
  Typography,
  Fade,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Tooltip,
  Box as MuiBox,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import {
  EmojiEvents as TrophyIcon,
  ErrorOutline as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Schedule as TimeIcon,
  Check as CorrectIcon,
  List as ListIcon,
  Close as IncorrectIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import CloseIcon from '@mui/icons-material/Close';
import katex from "katex";
import "katex/dist/katex.min.css";
import sanitizeHtml from "sanitize-html";
import { dark } from "@mui/material/styles/createPalette";
import { useContext } from "react";
import { ThemeContext } from '../context/ThemeContext';

const TFQuiz = () => {
  const { subfolder_id } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState(null); // null, "True", or "False"
  const [allAnswers, setAllAnswers] = useState([]);
  const [isQuizFinished, setIsQuizFinished] = useState(false);
  const [quizId, setQuizId] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isTimeUpModalOpen, setIsTimeUpModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isEditableReviewModalOpen, setIsEditableReviewModalOpen] = useState(false);
  const [isStartModalOpen, setIsStartModalOpen] = useState(true);
  const [maxAttempts, setMaxAttempts] = useState("");
  const [quizMaxAttempts, setQuizMaxAttempts] = useState(null);
  const [passingPercentage, setPassingPercentage] = useState("");
  const [negativeMarking, setNegativeMarking] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [currentAttemptNo, setCurrentAttemptNo] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [attemptAnswers, setAttemptAnswers] = useState({});
  const [animateKey, setAnimateKey] = useState(0);
  const [forceRender, setForceRender] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isTimerEnabled, setIsTimerEnabled] = useState(true); // New state for timer toggle
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [quizHistory, setQuizHistory] = useState({});
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedHistoryAttempt, setSelectedHistoryAttempt] = useState(null);
  const [reviewAnswersFromHistory, setReviewAnswersFromHistory] = useState([]); // Store answers for review from history
  // const { darkMode } = useContext(ThemeContext);
  const { mode, customColors } = useContext(ThemeContext);
  const [quizIds, setQuizIds] = useState([]);
  const [isHistoryReviewModalOpen, setIsHistoryReviewModalOpen] = useState(false); // state for history review modal
  // Add new state variables at the top of the TFQuiz component (unchanged from previous)
  const [isOfflineModalOpen, setIsOfflineModalOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [skippedQuestions, setSkippedQuestions] = useState(new Set());
  const [isQuestionListOpen, setIsQuestionListOpen] = useState(false);
  const [shouldShowStartModal, setShouldShowStartModal] = React.useState(true);
  const timerRef = useRef(null);
  const modalRef = useRef(null);
  const reviewModalRef = useRef(null);
  const editableReviewModalRef = useRef(null);
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const [openConfirm, setOpenConfirm] = useState(false);
  const [pendingUnansweredCount, setPendingUnansweredCount] = useState(0);

  // Debug modal state, questions length, and DOM
  useEffect(() => {
    console.log("isResultModalOpen:", isResultModalOpen);
    console.log("isReviewModalOpen:", isReviewModalOpen);
    console.log("isEditableReviewModalOpen:", isEditableReviewModalOpen);
    console.log("isStartModalOpen:", isStartModalOpen);
    console.log("Questions length:", questions.length);
    console.log("Force render count:", forceRender);
    if (isResultModalOpen && modalRef.current) {
      console.log("Result Modal DOM present:", modalRef.current);
    }
    if (isReviewModalOpen && reviewModalRef.current) {
      console.log("Review Modal DOM present:", reviewModalRef.current);
    }
    if (isEditableReviewModalOpen && editableReviewModalRef.current) {
      console.log("Editable Review Modal DOM present:", editableReviewModalRef.current);
    }
  }, [isResultModalOpen, isReviewModalOpen, isEditableReviewModalOpen, questions, forceRender]);

  // Fisher-Yates shuffle function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Fetch questions from /subfolder/${subfolder_id}/truefalse/
  useEffect(() => {
    if (!quizStarted) return;

    const fetchQuestions = async () => {
      try {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
          throw new Error("No auth token found in sessionStorage");
        }

        const folderId = parseInt(subfolder_id, 10);
        if (isNaN(folderId)) {
          throw new Error("Invalid subfolder_id: must be a number");
        }

        const maxAttemptsValue = maxAttempts ? parseInt(maxAttempts, 10) : null;
        const passingPercentageValue = passingPercentage
          ? parseFloat(passingPercentage)
          : 45;

        if (maxAttempts && (isNaN(maxAttemptsValue) || maxAttemptsValue <= 0)) {
          throw new Error("Invalid max attempts: must be a positive number or empty");
        }
        if (isNaN(passingPercentageValue) || passingPercentageValue < 0 || passingPercentageValue > 100) {
          throw new Error("Invalid passing percentage: must be between 0 and 100");
        }

        // Create quiz
        const quizResponse = await fetch(`${apiUrl}/quiz/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            folder: folderId,
            passing_percentage: passingPercentageValue,
            max_attempts: maxAttemptsValue,
          }),
        });

        if (!quizResponse.ok) {
          const errorText = await quizResponse.text();
          console.error(
            `Quiz creation failed: Status ${quizResponse.status}, ${errorText}`
          );
          throw new Error(`Failed to create quiz: ${quizResponse.statusText}`);
        }

        const quizData = await quizResponse.json();
        console.log("Quiz creation response:", quizData);
        const fetchedQuizId = quizData.id || quizData.quiz_id;
        if (!fetchedQuizId) {
          throw new Error("No quiz_id found in quiz creation response");
        }
        setQuizId(fetchedQuizId);
        setQuizMaxAttempts(quizData.max_attempts ?? null);

        // Fetch true/false questions
        const questionsResponse = await fetch(
          `${apiUrl}/subfolder/${subfolder_id}/truefalse/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!questionsResponse.ok) {
          const errorText = await questionsResponse.text();
          console.error(
            `Questions fetch failed: Status ${questionsResponse.status}, ${errorText}`
          );
          throw new Error(
            `Failed to fetch questions: ${questionsResponse.statusText}`
          );
        }

        const questionsData = await questionsResponse.json();
        console.log(
          "Raw questions response from /subfolder/truefalse/:",
          JSON.stringify(questionsData, null, 2)
        );

        const questionArray = Array.isArray(questionsData)
          ? questionsData
          : questionsData.questions || [];
        if (!Array.isArray(questionArray)) {
          console.error("Questions response is not an array:", questionsData);
          throw new Error("No valid questions found in response");
        }

        const fetchedQuestions = questionArray
          .map((item) => {
            console.log(
              `Fields for question ID ${item.id || item.question_id}:`,
              Object.keys(item)
            );

            const answer = item.answers; // Boolean (true/false)

            const questionObj = {
              id: item.id || item.question_id,
              question: item.statement || "No question provided",
              answer: answer, // Boolean
              explanation: item.explanation || "",
              question_type: item.question_type || "TRUEFALSE",
            };
            console.log("Mapped question:", {
              id: questionObj.id,
              question: questionObj.question,
              answer: questionObj.answer,
              type: questionObj.question_type,
              explanation: questionObj.explanation,
            });
            return questionObj;
          })
          .filter((item) => item && item.question_type === "TRUEFALSE" && item.id);

        console.log(
          "Filtered TRUEFALSE questions with IDs:",
          fetchedQuestions.map((q) => ({
            id: q.id,
            question: q.question,
            answer: q.answer,
          }))
        );
        if (fetchedQuestions.length === 0) {
          console.warn("No TRUEFALSE questions with valid IDs found");
          alert("No true/false questions available for this quiz.");
          return;
        }

        // Shuffle the questions
        const shuffledQuestions = shuffleArray(fetchedQuestions);
        console.log(
          "Shuffled questions:",
          shuffledQuestions.map((q) => ({
            id: q.id,
            question: q.question,
          }))
        );

        setQuestions(shuffledQuestions);
        setAllAnswers(new Array(shuffledQuestions.length).fill(null));
        setTimeLeft(shuffledQuestions.length * 30);
      } catch (error) {
        console.error("Error fetching questions:", error.message);
        alert("Failed to load quiz questions. Please try again.");
      }
    };

    fetchQuestions();
  }, [subfolder_id, quizStarted, maxAttempts, passingPercentage]);

  // Timer effect for the entire quiz
  useEffect(() => {
    if (isQuizFinished || timeLeft === null) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isQuizFinished, timeLeft]);

  // Load saved answer when navigating to a question
  useEffect(() => {
    setUserAnswer(allAnswers[currentQuestionIndex] ?? null);
  }, [currentQuestionIndex, allAnswers]);

  // Handle time up
  const handleTimeUp = () => {
    if (!isTimerEnabled) return; // Prevent time-up logic when timer is disabled

    if (userAnswer !== null) {
      const newAnswers = [...allAnswers];
      newAnswers[currentQuestionIndex] = userAnswer;
      console.log(
        "Time up - Saved answer:",
        userAnswer,
        "for question",
        currentQuestionIndex + 1
      );
      console.log("All answers:", newAnswers);
      setAllAnswers(newAnswers);
    }
    setIsTimeUpModalOpen(true);
    setUserAnswer(null);
  };

  // Handle answer selection
  const handleSelectAnswer = (answer) => {
    const capitalizedAnswer = answer ? "True" : "False";
    setUserAnswer(capitalizedAnswer);
    console.log("Selected answer for question", currentQuestionIndex + 1, ":", capitalizedAnswer);
  };

  // Handle Previous Question
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      const newAnswers = [...allAnswers];
      newAnswers[currentQuestionIndex] = userAnswer; // Save current answer (or null)
      setAllAnswers(newAnswers);
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setUserAnswer(allAnswers[currentQuestionIndex - 1] ?? null); // Load saved answer
    }
  };

  // Handle Next Question
  const handleNextQuestion = () => {
    if (currentQuestionIndex + 1 < questions.length) {
      const newAnswers = [...allAnswers];
      newAnswers[currentQuestionIndex] = userAnswer; // Save current answer (or null if not selected)
      setAllAnswers(newAnswers);
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setUserAnswer(allAnswers[currentQuestionIndex + 1] ?? null); // Load saved answer or reset
    }
  };

  // Handle Skip Question to track skipped questions
  const handleSkipQuestion = () => {
    const newAnswers = [...allAnswers];
    newAnswers[currentQuestionIndex] = null; // Clear current answer
    setAllAnswers(newAnswers);
    setSkippedQuestions((prev) => new Set(prev).add(currentQuestionIndex));

    if (currentQuestionIndex + 1 < questions.length) {
      // Move to next question if not the last one
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setUserAnswer(allAnswers[currentQuestionIndex + 1] ?? null); // Load saved answer or reset
    } else {
      // If last question, open editable review modal
      setIsEditableReviewModalOpen(true);
    }
  };


  // Handler to toggle question list visibility
  const toggleQuestionList = () => {
    setIsQuestionListOpen((prev) => !prev);
  };

  // Handler to navigate to a specific question
  const handleQuestionSelect = (index) => {
    const newAnswers = [...allAnswers];
    newAnswers[currentQuestionIndex] = userAnswer; // Save current answer (or null)
    setAllAnswers(newAnswers);
    setCurrentQuestionIndex(index);
    setUserAnswer(allAnswers[index] ?? null); // Load saved answer or reset
    setIsQuestionListOpen(false);
  };

  // Cache Storage name 
  const CACHE_NAME = 'quiz-answers-cache';

  // Monitor network status (unchanged)
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Save answers to Cache Storage 
  const saveAnswersToCache = async (answers) => {
    try {
      const cache = await caches.open(CACHE_NAME);
      const cacheKey = `quiz-${quizId}-answers`;
      const cacheData = {
        quizId,
        answers,
        timestamp: new Date().toISOString(),
      };
      await cache.put(
        cacheKey,
        new Response(JSON.stringify(cacheData), {
          headers: { 'Content-Type': 'application/json' },
        })
      );
      console.log('Answers saved to cache:', cacheData);
    } catch (error) {
      console.error('Error saving answers to cache:', error);
    }
  };

  // Check and submit cached answers (new function to be called in handleFinishQuiz)
  const submitCachedAnswers = async () => {
    try {
      const cache = await caches.open(CACHE_NAME);
      const cacheKey = `quiz-${quizId}-answers`;
      const cachedResponse = await cache.match(cacheKey);

      if (cachedResponse) {
        const cachedData = await cachedResponse.json();
        if (cachedData.quizId === quizId) {
          console.log('Submitting cached answers:', cachedData.answers);
          await submitQuizResults(cachedData.answers);
          await cache.delete(cacheKey); // Clear cache after successful submission
          console.log('Cached answers submitted and cleared');
          return true; // Indicate successful submission
        }
      }
      return false; // No cached answers or submission not needed
    } catch (error) {
      console.error('Error submitting cached answers:', error);
      return false;
    }
  };

  //  handleFinishQuiz function
  // Helper to submit quiz
  const finishQuizSubmission = async (answers) => {
    console.log("Finishing quiz with answers:", answers);

    if (!isOnline) {
      await saveAnswersToCache(answers);
      setIsOfflineModalOpen(true);
    } else {
      const submittedCached = await submitCachedAnswers();
      if (!submittedCached) {
        submitQuizResults(answers);
      }
    }
  };

  // Finish button handler
  const handleFinishQuiz = async () => {
    const newAnswers = [...allAnswers];
    newAnswers[currentQuestionIndex] = userAnswer; // Save current answer
    setAllAnswers(newAnswers);

    const unansweredCount = newAnswers.filter(
      (ans) => ans === null || ans === undefined
    ).length;

    if (unansweredCount > 0) {
      // Store unanswered count in state & open confirm dialog
      setPendingUnansweredCount(unansweredCount);
      setOpenConfirm(true);
    } else {
      // Submit directly if no unanswered questions
      await finishQuizSubmission(newAnswers);
    }
  };

  // Confirm submit from dialog
  const handleConfirmSubmit = async () => {
    setOpenConfirm(false);
    await finishQuizSubmission(allAnswers);
  };


  // Handle Review Answers (opens editable modal)
  const handleReviewAnswers = () => {
    console.log("Opening editable review modal");
    setIsEditableReviewModalOpen(true);
  };

  // Handle Save and Finish from editable review modal
  const handleSaveAndFinish = () => {
    console.log("Saving edited answers and finishing quiz:", allAnswers);
    submitQuizResults(allAnswers);
    setIsEditableReviewModalOpen(false);
  };

  // Handle answer edit in editable review modal
  const handleAnswerEdit = (index, value) => {
    const newAnswers = [...allAnswers];
    newAnswers[index] = value === "True" ? "True" : value === "False" ? "False" : null;
    setAllAnswers(newAnswers);
    console.log("Edited answer for question", index + 1, ":", value);
  };

  useEffect(() => {
    if (!isTimerEnabled || isQuizFinished || timeLeft === null || timeLeft === 0) {
      if (timerRef.current) {
        console.log("Clearing timer:", timerRef.current);
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    console.log("Setting up timer with timeLeft:", timeLeft);
    if (timerRef.current) {
      console.log("Clearing existing timer before new setup:", timerRef.current);
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        console.log("Timer tick, prev timeLeft:", prev);
        if (prev <= 1) {
          console.log("Time up, clearing timer:", timerRef.current);
          clearInterval(timerRef.current);
          timerRef.current = null;
          if (isTimerEnabled) {
            handleTimeUp();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        console.log("Cleanup timer:", timerRef.current);
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isTimerEnabled, isQuizFinished,]);


  // Handle Start Quiz
  const handleStartQuiz = () => {
    const maxAttemptsValue = maxAttempts ? parseInt(maxAttempts, 10) : null;
    if (maxAttempts && (isNaN(maxAttemptsValue) || maxAttemptsValue <= 1)) {
      alert("Please enter a valid number of max attempts (positive number or leave blank for unlimited).");
      return;
    }

    if (passingPercentage) {
      const percentage = parseFloat(passingPercentage);
      if (isNaN(percentage) || percentage < 1 || percentage > 100) {
        alert("Please enter a valid passing percentage between 1 and 100.");
        return;
      }
    }

    if (!passingPercentage) {
      setPassingPercentage("35");
    }

    if (isTimerEnabled) {
      setTimeLeft(questions.length * 30 || 1800);
    } else {
      setTimeLeft(null);
    }

    setQuizStarted(true);
    setIsStartModalOpen(false); // Close start modal when quiz starts
  };

  // Handle Retry Quiz
  const handleRetryQuiz = () => {
    const max = quizMaxAttempts ?? null;
    if (max !== null && attemptCount >= max) {
      alert("You have reached the maximum number of attempts.");
      return;
    }
    setIsQuizFinished(false);
    setCurrentQuestionIndex(0);
    setUserAnswer(null);
    setAllAnswers(new Array(questions.length).fill(null));
    setTimeLeft(questions.length * 30);
    setResults(null);
    setError(null);
    setQuizStarted(true);
  };

  // Handle Close Quiz
  const handleCloseQuiz = () => {
    if (isStartModalOpen && !quizStarted) {
      setIsStartModalOpen(false);
      navigate(-1);
    } else if (quizStarted && !isQuizFinished) {
      setIsCloseDialogOpen(true);
    } else {
      navigate(-1);
    }
  };

  // Handle Confirm Close
  const handleConfirmClose = () => {
    setIsCloseDialogOpen(false);
    navigate(-1);
  };

  // Handle Cancel Close
  const handleCancelClose = () => {
    setIsCloseDialogOpen(false);
  };

  // Load quiz history from localStorage when component mounts
  useEffect(() => {
    try {
      // const storedHistory = localStorage.getItem('quizHistory');
      const storedQuizIds = localStorage.getItem('quizIds');
      // if (storedHistory) {
      //   setQuizHistory(JSON.parse(storedHistory));
      // }
      if (storedQuizIds) {
        setQuizIds(JSON.parse(storedQuizIds));
      }
    } catch (error) {
      console.error('Error loading quiz history or IDs from localStorage:', error);
      setQuizHistory({});
      setQuizIds([]);
    }
  }, []);

  // Submit quiz results to API
  const submitQuizResults = async (answers = allAnswers) => {
    if (!quizId) {
      setError("Quiz ID is missing. Cannot submit results.");
      alert("Quiz ID is missing. Cannot submit results.");
      return;
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      const submitUrl = `${apiUrl}/quiz/${quizId}/submit/`;
      const payload = {
        answers: answers.map((answer, index) => ({
          question_id: Number(questions[index].id),
          selected_answer: answer !== null ? answer : "",
        })),
      };
      console.log("Submitting payload:", JSON.stringify(payload, null, 2));
      const response = await fetch(submitUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Submission failed: Status ${response.status}, ${errorText}`);
        throw new Error(`Failed to submit quiz: ${response.statusText}`);
      }
      const resultData = await response.json();
      console.log("Raw API response:", JSON.stringify(resultData, null, 2));

      const attemptNo = resultData.attempt_no || resultData.attempt_number || resultData.id || null;
      if (attemptNo !== null) {
        setCurrentAttemptNo(attemptNo);
        setAttemptCount(attemptNo);
        setAttemptAnswers((prev) => ({
          ...prev,
          [attemptNo]: [...answers],
        }));
      }

      let correctAnswers = 0;
      let wrongAnswers = 0;
      const attemptedQuestions = answers.filter((answer) => answer !== null).length;

      answers.forEach((answer, index) => {
        if (answer !== null && answer.trim() !== "") {
          const isCorrect = answer.toLowerCase() === questions[index].answer.toString().toLowerCase();
          if (isCorrect) {
            correctAnswers += 1;
          } else {
            wrongAnswers += 1;
          }
        }
      });

      const totalTFQuestions = questions.length;
      let finalScore;
      let scorePercentage;
      if (negativeMarking) {
        finalScore = correctAnswers - wrongAnswers;
      } else {
        finalScore = correctAnswers;
      }
      scorePercentage = totalTFQuestions > 0 ? (finalScore / totalTFQuestions) * 100 : 0;

      const passingPercentageValue = resultData.passing_percentage ?? passingPercentage ?? 45;
      const result = scorePercentage >= passingPercentageValue ? "Passed" : "Failed";

      const newResults = {
        final_score: finalScore,
        score_percentage: scorePercentage,
        result: result,
        attempted_questions: attemptedQuestions,
        attempts_used: attemptNo ?? attemptCount + 1,
        correct_answers: correctAnswers,
        wrong_answers: wrongAnswers,
        started_at: resultData.started_at ?? "N/A",
        ended_at: resultData.ended_at ?? "N/A",
        quiz_status: resultData.quiz_status ?? "N/A",
        total_questions: totalTFQuestions,
        passing_percentage: passingPercentageValue,
      };

      // Save quizId and quizHistory to localStorage
      setQuizIds((prev) => {
        const updatedQuizIds = prev.includes(quizId) ? prev : [...prev, quizId];
        try {
          localStorage.setItem('quizIds', JSON.stringify(updatedQuizIds));
          console.log('Saved quizIds:', updatedQuizIds);
        } catch (error) {
          console.error('Error saving quiz IDs to localStorage:', error);
        }
        return updatedQuizIds;
      });

      // setQuizHistory((prev) => {
      //   const existingAttempts = prev[quizId] || [];
      //   const attemptExists = existingAttempts.some((attempt) => attempt.attempt_no === (attemptNo ?? attemptCount + 1));
      //   if (attemptExists) {
      //     console.warn(`Attempt ${attemptNo ?? attemptCount + 1} already exists for quiz ${quizId}`);
      //     return prev;
      //   }
      //   const newAttempt = {
      //     attempt_no: attemptNo ?? attemptCount + 1,
      //     results: newResults,
      //     answers: [...answers],
      //     timestamp: new Date().toISOString(),
      //   };
      //   const updatedHistory = {
      //     ...prev,
      //     [quizId]: [...existingAttempts, newAttempt],
      //   };
      //   try {
      //     localStorage.setItem('quizHistory', JSON.stringify(updatedHistory));
      //     console.log('Saved quizHistory:', updatedHistory);
      //   } catch (error) {
      //     console.error('Error saving quiz history to localStorage:', error);
      //   }
      //   return updatedHistory;
      // });

      setResults(newResults);
      setIsQuizFinished(true);
      setError(null);
    } catch (error) {
      console.error("Error submitting quiz:", error.message);
      setError(`Failed to submit quiz: ${error.message}`);
      setAttemptCount((prev) => prev + 1);
      alert(`Failed to submit quiz: ${error.message}`);
    }
  };

  // Fetch quiz results from API
  const fetchQuizResults = async (quizId) => {
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      const response = await fetch(`${apiUrl}/quiz/${quizId}/result/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch results for quiz ${quizId}: ${response.statusText}`);
      }
      const resultData = await response.json();
      const attempts = Array.isArray(resultData) ? resultData : [resultData];
      const formattedAttempts = attempts.map((result, index) => {
        const attemptNo = result.attempt_no || result.attempt_number || index + 1;
        const answers = result.answers ? result.answers.map((ans) => ans.selected_answer || "") : [];
        return {
          attempt_no: attemptNo,
          results: {
            final_score: result.final_score ?? result.score ?? 0,
            score_percentage: result.score_percentage ?? (result.score && result.total_questions ? (result.score / result.total_questions) * 100 : 0),
            result: result.result ?? (result.score_percentage >= (result.passing_percentage ?? 45) ? "Passed" : "Failed"),
            attempted_questions: result.attempted_questions ?? answers.filter((a) => a !== "").length,
            attempts_used: attemptNo,
            correct_answers: result.correct_answers ?? 0,
            wrong_answers: result.wrong_answers ?? 0,
            started_at: result.started_at ?? "N/A",
            ended_at: result.ended_at ?? "N/A",
            quiz_status: result.quiz_status ?? "N/A",
            total_questions: result.total_questions ?? questions.length,
            passing_percentage: result.passing_percentage ?? 45,
          },
          answers,
          timestamp: result.timestamp ?? new Date().toISOString(),
        };
      });
      console.log(`Fetched results for quiz ${quizId}:`, formattedAttempts);
      return formattedAttempts;
    } catch (error) {
      console.error(`Error fetching results for quiz ${quizId}:`, error);
      return [];
    }
  };

  // Toggle history drawer
  // Toggle history drawer
  // const toggleHistoryDrawer = (open) => async () => {
  //   setIsHistoryOpen(open);
  //   if (open) {
  //     setIsStartModalOpen(false);
  //     setShouldShowStartModal(false);
  //     setQuizHistory((prev) => {
  //       const updatedHistory = { ...prev };
  //       const fetchPromises = quizIds.map(async (qId) => {
  //         const results = await fetchQuizResults(qId);
  //         const existingAttempts = updatedHistory[qId] || [];
  //         const newAttempts = results.filter(
  //           (newAttempt) =>
  //             !existingAttempts.some(
  //               (existing) => existing.attempt_no === newAttempt.attempt_no
  //             )
  //         );
  //         updatedHistory[qId] = [...existingAttempts, ...newAttempts];
  //       });
  //       Promise.all(fetchPromises).then(() => {
  //         try {
  //           localStorage.setItem('quizHistory', JSON.stringify(updatedHistory));
  //           console.log('Updated quizHistory after fetch:', updatedHistory);
  //         } catch (error) {
  //           console.error('Error saving quiz history to localStorage:', error);
  //         }
  //       });
  //       return updatedHistory;
  //     });
  //   } else {
  //     if (!quizStarted && !isQuizFinished) {
  //       setIsStartModalOpen(true);
  //       setShouldShowStartModal(true);
  //     }
  //   }
  // };

  // Reopen Start Quiz Modal
  const handleOpenStartModal = () => {
    setIsHistoryOpen(false);
    setIsStartModalOpen(true);
    setShouldShowStartModal(true);
  };

  // Handle selecting a history attempt
  const handleSelectHistoryAttempt = (qId, attempt) => {
    setSelectedHistoryAttempt({ qId, attempt });
    const answers = attempt.answers || [];
    setReviewAnswersFromHistory(answers);
    console.log("Selected attempt answers:", answers);
    setIsHistoryModalOpen(true);
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'N/A';
    }
  };

  // Close history modal
  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedHistoryAttempt(null);
    if (!quizStarted && !isQuizFinished && shouldShowStartModal) {
      setIsStartModalOpen(true)
    }
  };

  // Fetch available attempts
  const fetchAvailableAttempts = async () => {
    if (!quizId) {
      setError("Quiz ID is missing. Cannot fetch attempts.");
      return [];
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      const maxAttemptsValue = quizMaxAttempts ?? attemptCount;
      const attemptPromises = [];
      for (let i = 1; i <= maxAttemptsValue; i++) {
        attemptPromises.push(
          fetch(`${apiUrl}/quiz/${quizId}/attempt/${i}/`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }).then(async (response) => {
            if (response.ok) {
              const data = await response.json();
              return { attempt_no: i, data };
            }
            return null;
          })
        );
      }
      const results = await Promise.all(attemptPromises);
      const validAttempts = results
        .filter((result) => result !== null)
        .map((result) => ({
          attempt_no: result.attempt_no,
          data: result.data,
        }));
      return validAttempts;
    } catch (error) {
      setError(`Failed to fetch attempts: ${error.message}`);
      return [];
    }
  };

  // Fetch attempt details
  const fetchAttemptDetails = async (attemptNo) => {
    if (!quizId || !attemptNo) {
      setError("Quiz ID or attempt number is missing.");
      return null;
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      const response = await fetch(`${apiUrl}/quiz/${quizId}/attempt/${attemptNo}/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch attempt ${attemptNo}: ${response.statusText}`);
      }
      const resultData = await response.json();
      return resultData;
    } catch (error) {
      setError(`Failed to fetch attempt ${attemptNo}: ${error.message}`);
      return null;
    }
  };

  // Fetch detailed results
  const fetchDetailedResults = async () => {
    if (!quizId) {
      setError("Quiz ID is missing. Cannot fetch results.");
      return null;
    }
    try {
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found");
      }
      const response = await fetch(`${apiUrl}/quiz/${quizId}/result/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch detailed results: ${response.statusText}`);
      }
      const resultData = await response.json();
      return resultData;
    } catch (error) {
      setError(`Failed to fetch detailed results: ${error.message}`);
      return null;
    }
  };

  // Handle View Result
  const handleViewResult = async () => {
    const availableAttempts = await fetchAvailableAttempts();
    setAttempts(availableAttempts);

    let resultData = null;
    let selectedAttemptNum = null;
    let attemptSpecificAnswers = [];

    if (availableAttempts.length > 0) {
      const latestAttempt = availableAttempts[availableAttempts.length - 1];
      selectedAttemptNum = latestAttempt.attempt_no;
      setSelectedAttempt(selectedAttemptNum);
      resultData = latestAttempt.data;

      attemptSpecificAnswers = attemptAnswers[selectedAttemptNum] || [];
      if (!attemptSpecificAnswers.length) {
        const attemptDetails = await fetchAttemptDetails(selectedAttemptNum);
        attemptSpecificAnswers = attemptDetails?.answers
          ? attemptDetails.answers.map((ans) => ans.selected_answer || "")
          : allAnswers;
      }
    } else {
      resultData = await fetchDetailedResults();
      attemptSpecificAnswers = allAnswers;
    }

    if (!resultData) {
      setIsResultModalOpen(true);
      setForceRender((prev) => prev + 1);
      return;
    }

    let correctAnswers = 0;
    let wrongAnswers = 0;
    const attemptedQuestions = attemptSpecificAnswers.filter(
      (answer) => answer !== null
    ).length;

    attemptSpecificAnswers.forEach((answer, index) => {
      if (index >= questions.length) return;
      if (answer !== null && answer.trim() !== "") {
        const isCorrect = answer.toLowerCase() === questions[index].answer.toString().toLowerCase();
        if (isCorrect) {
          correctAnswers += 1;
        } else {
          wrongAnswers += 1;
        }
      }
    });

    const totalTFQuestions = questions.length;
    let finalScore;
    let scorePercentage;
    if (negativeMarking) {
      finalScore = correctAnswers - wrongAnswers;
    } else {
      finalScore = correctAnswers;
    }
    scorePercentage = totalTFQuestions > 0 ? (finalScore / totalTFQuestions) * 100 : 0;

    const passingPercentageValue =
      resultData.passing_percentage ?? results?.passing_percentage ?? passingPercentage ?? 45;
    const result = scorePercentage >= passingPercentageValue ? "Passed" : "Failed";

    const newResults = {
      final_score: finalScore,
      score_percentage: scorePercentage,
      result: result,
      attempted_questions: attemptedQuestions,
      attempts_used: resultData.attempt_no ?? resultData.attempt_number ?? currentAttemptNo ?? attemptCount,
      correct_answers: correctAnswers,
      wrong_answers: wrongAnswers,
      started_at: resultData.started_at ?? results?.started_at ?? "N/A",
      ended_at: resultData.ended_at ?? results?.ended_at ?? "N/A",
      quiz_status: resultData.quiz_status ?? results?.quiz_status ?? "N/A",
      total_questions: totalTFQuestions,
      passing_percentage: passingPercentageValue,
    };

    setResults(newResults);
    setQuizMaxAttempts(resultData.max_attempts ?? quizMaxAttempts ?? null);
    setIsResultModalOpen(true);
    setForceRender((prev) => prev + 1);
    setAnimateKey((prev) => prev + 1);
    setError(null);
  };

  // Handle Attempt Change
  const handleAttemptChange = async (event) => {
    const attemptNo = event.target.value;
    setSelectedAttempt(attemptNo);

    const attemptData = await fetchAttemptDetails(attemptNo);
    if (!attemptData) {
      setForceRender((prev) => prev + 1);
      return;
    }

    let attemptSpecificAnswers = attemptAnswers[attemptNo] || [];
    if (!attemptSpecificAnswers.length) {
      attemptSpecificAnswers = attemptData.answers
        ? attemptData.answers.map((ans) => ans.selected_answer || "")
        : allAnswers;
    }

    let correctAnswers = 0;
    let wrongAnswers = 0;
    const attemptedQuestions = attemptSpecificAnswers.filter(
      (answer) => answer !== null
    ).length;

    attemptSpecificAnswers.forEach((answer, index) => {
      if (index >= questions.length) return;
      if (answer !== null && answer.trim() !== "") {
        const isCorrect = answer.toLowerCase() === questions[index].answer.toString().toLowerCase();
        if (isCorrect) {
          correctAnswers += 1;
        } else {
          wrongAnswers += 1;
        }
      }
    });

    const totalTFQuestions = questions.length;
    let finalScore;
    let scorePercentage;
    if (negativeMarking) {
      finalScore = correctAnswers - wrongAnswers;
    } else {
      finalScore = correctAnswers;
    }
    scorePercentage = totalTFQuestions > 0 ? (finalScore / totalTFQuestions) * 100 : 0;

    const passingPercentageValue =
      attemptData.passing_percentage ?? results?.passing_percentage ?? passingPercentage ?? 45;
    const result = scorePercentage >= passingPercentageValue ? "Passed" : "Failed";

    const newResults = {
      final_score: finalScore,
      score_percentage: scorePercentage,
      result: result,
      attempted_questions: attemptedQuestions,
      attempts_used: attemptData.attempt_no ?? attemptData.attempt_number ?? attemptNo,
      correct_answers: correctAnswers,
      wrong_answers: wrongAnswers,
      started_at: attemptData.started_at ?? results?.started_at ?? "N/A",
      ended_at: attemptData.ended_at ?? results?.ended_at ?? "N/A",
      quiz_status: attemptData.quiz_status ?? results?.quiz_status ?? "N/A",
      total_questions: totalTFQuestions,
      passing_percentage: passingPercentageValue,
    };

    setResults(newResults);
    setQuizMaxAttempts(attemptData.max_attempts ?? quizMaxAttempts ?? null);
    setForceRender((prev) => prev + 1);
    setAnimateKey((prev) => prev + 1);
    setError(null);
  };

  // Open history review modal
  const handleOpenHistoryReviewModal = () => {
    if (!selectedHistoryAttempt || !selectedHistoryAttempt.attempt.answers || selectedHistoryAttempt.attempt.answers.length === 0) {
      console.warn("No valid answers available for review in history");
      setReviewAnswersFromHistory([]);
    }
    setIsHistoryReviewModalOpen(true);
    setIsHistoryModalOpen(false);
    setForceRender((prev) => prev + 1);
  };

  // Close history review modal
  const handleCloseHistoryReviewModal = () => {
    setIsHistoryReviewModalOpen(false);
    setIsHistoryModalOpen(true);
    setForceRender((prev) => prev + 1);
  };

  // Open result review modal
  const handleOpenReviewModal = () => {
    console.log("Opening non-editable review modal");
    console.log("Review data:", { questions, allAnswers });
    setIsReviewModalOpen(true);
    setIsResultModalOpen(false);
    setForceRender((prev) => prev + 1);
  };

  // Close result review modal
  const handleCloseReviewModal = () => {
    console.log("Closing non-editable review modal");
    setIsReviewModalOpen(false);
    setIsResultModalOpen(true);
    setForceRender((prev) => prev + 1);
  };

  // Close answer review modal
  const handleCloseEditableReviewModal = () => {
    console.log("Closing editable review modal");
    setIsEditableReviewModalOpen(false);
    setForceRender((prev) => prev + 1);
  };

  // Close results modal
  const handleCloseResultModal = () => {
    console.log("Closing result modal");
    setIsResultModalOpen(false);
    setCurrentPage(1);
    setForceRender((prev) => prev + 1);
  };

  // Close time-up modal and submit results
  const handleCloseModal = async () => {
    if (!isOnline) {
      setIsOfflineModalOpen(true); // Open offline modal if no internet
      await saveAnswersToCache(allAnswers); // Cache answers
    } else {
      setIsTimeUpModalOpen(false); // Close timer modal
      const submittedCached = await submitCachedAnswers();
      if (!submittedCached) {
        submitQuizResults(allAnswers); // Submit current answers if no cached answers
      }
    }
  };

  // Format time in MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Format percentage
  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return "N/A";
    const num = Number(percentage);
    if (Number.isInteger(num)) return `${num}%`;
    return `${num.toFixed(num % 1 === 0 ? 0 : num < 10 ? 2 : 1)}%`;
  };

  // Format ISO date to readable format
  const formatDate = (isoString) => {
    if (!isoString || isoString === "N/A") {
      return "N/A";
    }
    return new Date(isoString).toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Result fields for display
  const resultFields = results
    ? [
      {
        label: "Final Score",
        value: `${results.final_score ?? "N/A"} out of ${results.total_questions}`,
        icon: TrophyIcon,
      },
      {
        label: "Attempts Used",
        value: `${results.attempts_used ?? attemptCount} out of ${quizMaxAttempts !== null ? quizMaxAttempts : maxAttempts === "" ? "Unlimited" : maxAttempts}`,
        icon: InfoIcon,
      },
      {
        label: "Score Percentage",
        value: formatPercentage(results.score_percentage),
        icon: CheckIcon,
      },
      {
        label: "Result",
        value: isQuizFinished
          ? `${results.result ?? "N/A"}${results.passing_percentage ? ` (Passing: ${formatPercentage(results.passing_percentage)})` : ""}`
          : results.result ?? "N/A",
        icon: CheckIcon,
        bgcolor: results.result?.toLowerCase() === "passed" ? "#e8f5e9" : results.result?.toLowerCase() === "failed" ? "#ffebee" : "#f0f4f8",
        color: results.result?.toLowerCase() === "passed" ? "#2e7d32" : results.result?.toLowerCase() === "failed" ? "#d32f2f" : "#01579b",
      },
      {
        label: "Attempted Questions",
        value: `${results.attempted_questions ?? 0} out of ${results.total_questions}`,
        icon: CheckIcon,
      },
      ...(isResultModalOpen
        ? [
          {
            label: "Total Questions",
            value: results.total_questions,
            icon: InfoIcon,
          },
          {
            label: "Correct Answers",
            value: results.correct_answers ?? "N/A",
            icon: CorrectIcon,
          },
          {
            label: "Wrong Answers",
            value: results.wrong_answers ?? "N/A",
            icon: IncorrectIcon,
          },
          {
            label: "Max Attempts",
            value: quizMaxAttempts ?? "Unlimited",
            icon: InfoIcon,
          },
          {
            label: "Quiz Status",
            value: results.quiz_status ?? "N/A",
            icon: InfoIcon,
          },
          {
            label: "Started At",
            value: formatDate(results.started_at),
            icon: TimeIcon,
          },
          {
            label: "Ended At",
            value: formatDate(results.ended_at),
            icon: TimeIcon,
          },
        ]
        : []),
    ]
    : [];

  const itemsPerPage = 6;
  const totalPages = Math.ceil(resultFields.length / itemsPerPage);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  // Render content with LaTeX and HTML
  const renderContent = (html) => {
    if (!html) return null;

    const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g);

    return (
      <div>
        {parts.map((part, index) => {
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2);
            try {
              const renderedLatex = katex.renderToString(latex, {
                displayMode: true,
              });
              return (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content block-latex"
                />
              );
            } catch (error) {
              console.error("Block LaTeX rendering error:", error);
              return <div key={index} className="error-content">{part}</div>;
            }
          }

          if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1);
            try {
              const renderedLatex = katex.renderToString(latex, {
                displayMode: false,
              });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content inline-latex"
                />
              );
            } catch (error) {
              console.error("Inline LaTeX rendering error:", error);
              return <span key={index} className="error-content">{part}</span>;
            }
          }

          try {
            const formattedContent = part.replace(/\n/g, "<br />");
            const allowedTags = [
              "b",
              "i",
              "em",
              "strong",
              "span",
              "br",
              "u",
              "sub",
              "sup",
              "a",
              "p",
              "img",
            ];
            const allowedAttributes = {
              img: ["src", "alt"],
              a: ["href", "target"],
              span: ["style"],
              p: ["style"],
            };

            const sanitizedContent = sanitizeHtml(formattedContent, {
              allowedTags,
              allowedAttributes,
              transformTags: {
                p: "span",
                div: "span",
              },
            });

            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                className="rendered-content inline-text"
              />
            );
          } catch (error) {
            console.error("HTML rendering error:", error);
            return <span key={index} className="error-content">{part}</span>;
          }
        })}
      </div>
    );
  };

  // Render
  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {/* {(!quizStarted || isQuizFinished) && (
        <IconButton
          aria-label="history"
          onClick={toggleHistoryDrawer(true)}
          sx={{
            position: "fixed",
            top: 16,
            right: 80,
            zIndex: 10001,
            backgroundColor: darkMode ? "rgba(134, 133, 133, 0.8)" : "rgba(255, 255, 255, 0.8)",
            "&:hover": {
              backgroundColor: darkMode ? "rgba(145, 128, 128, 1)" : "rgba(255, 255, 255, 1)",
            },
          }}
        >
          <HistoryIcon />
        </IconButton>
      )} */}
      {quizStarted && !isQuizFinished && (
        <IconButton
          aria-label="questions"
          onClick={toggleQuestionList}
          sx={{
            position: "fixed",
            top: 16,
            right: 80, // Positioned before close icon
            zIndex: 10001, // Ensure it's above other elements but below drawer
            backgroundColor: mode === "dark" ? "rgba(134, 133, 133, 0.8)" : "rgba(255, 255, 255, 0.8)",
            "&:hover": {
              backgroundColor: mode === "dark" ? "rgba(145, 128, 128, 1)" : "rgba(255, 255, 255, 1)",
            },
          }}
        >
          <ListIcon />
        </IconButton>
      )}
      <IconButton
        aria-label="close quiz"
        onClick={handleCloseQuiz}
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          zIndex: 10001,
          backgroundColor: mode === "dark" ? "rgba(134, 133, 133, 0.8)" : "rgba(255, 255, 255, 0.8)",
          "&:hover": {
            backgroundColor: mode === "dark" ? "rgba(145, 128, 128, 1)" : "rgba(255, 255, 255, 1)",
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Question List Modal */}
      <Modal
        open={isQuestionListOpen}
        onClose={toggleQuestionList}
        aria-labelledby="question-list-modal"
        sx={{ zIndex: 10001 }}
      >
        <Fade in={isQuestionListOpen}>
          <MuiBox
            sx={{
              position: "fixed",
              top: 64,
              right: 80,
              width: { xs: "80%", sm: 300 },
              bgcolor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
              p: { xs: 2, sm: 3 },
              background: mode === "dark" ? "linear-gradient(145deg, #212121, #424242)" : mode === "custom" ? customColors.background : "linear-gradient(145deg, #ffffff, #f0f4f8)",
              maxHeight: "60vh",
              overflowY: "auto",
            }}
          >
            <Typography
              id="question-list-modal"
              variant="h6"
              sx={{
                fontWeight: 600,
                color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd",
                mb: 2,
                textAlign: "center",
              }}
            >
              Question List
            </Typography>
            <MuiBox
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
                gap: 1,
                justifyItems: "center",
                mb: 2,
              }}
            >
              {questions.map((_, index) => {
                const isAttempted = allAnswers[index] !== null;
                const isSkipped = allAnswers[index] === null && currentQuestionIndex > index;
                return (
                  <MuiBox
                    key={index}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        mode === "dark"
                          ? isAttempted
                            ? "#1b5e20" // dark green attempted
                            : isSkipped
                              ? "#4a1c1c" // dark red skipped
                              : "#2c2c2c" // dark neutral
                          : mode === "custom"
                            ? isAttempted
                              ? customColors.success || "#aefab4" // custom attempted
                              : isSkipped
                                ? customColors.error || "#f8bbc4" // custom skipped
                                : "#ffffff" // custom neutral
                            : isAttempted
                              ? "#aefab4" // light attempted
                              : isSkipped
                                ? "#f8bbc4" // light skipped
                                : "transparent", // light neutral  
                      border: isAttempted || isSkipped ? "none" : "1px solid #b0bec5",
                      cursor: "pointer",
                      fontWeight: 500,
                      color:
                        mode === "dark"
                          ? isAttempted
                            ? "#65e76b" // dark attempted text
                            : isSkipped
                              ? "#fc6767" // dark skipped text
                              : "#E0E0E0" // dark neutral text
                          : mode === "custom"
                            ? "#000000" // ✅ always black in custom mode
                            : isAttempted
                              ? "#2e7d32" // light attempted text
                              : isSkipped
                                ? "#d32f2f" // light skipped text
                                : "#333",   // light neutral text


                      "&:hover": {
                        bgcolor:
                          mode === "dark"
                            ? isAttempted
                              ? "#376e3a" // dark green hover
                              : isSkipped
                                ? "#7F1D1D" // dark red hover
                                : "#333" // dark neutral hover
                            : mode === "custom"
                              ? isAttempted
                                ? customColors.successHover || "#d0eddb"
                                : isSkipped
                                  ? customColors.errorHover || "#ffccd5"
                                  : customColors.hover || "#f0f4f8"
                              : isAttempted
                                ? "#d0eddb" // light green hover
                                : isSkipped
                                  ? "#ffccd5" // light red hover
                                  : "#f0f4f8", // light neutral hover
                      },
                    }}
                    onClick={() => handleQuestionSelect(index)}
                  >
                    {index + 1}
                  </MuiBox>
                );
              })}
            </MuiBox>
            <MuiBox sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                sx={{
                  borderColor: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd",
                  color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd",
                  borderRadius: "50px",
                  px: 4,
                  py: 1,
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#01579b",
                    color: "#01579b",
                  },
                }}
                onClick={toggleQuestionList}
              >
                Close
              </Button>
            </MuiBox>
          </MuiBox>
        </Fade>
      </Modal>

      {/* History Drawer */}
      {/* <Drawer
        anchor="right"
        open={isHistoryOpen}
        onClose={toggleHistoryDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "80%", sm: 300 },
            bgcolor: darkMode ? "#3b3a3a" : "#f9fafb",
            p: 2,
            zIndex: 11000,
          },
        }}
      >
        <MuiBox sx={{ display: "flex", alignItems: "center", mb: 2, p: 2 }}>
          <HistoryIcon sx={{ color: "#0277bd", mr: 1 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#0277bd",
            }}
          >
            Quiz History
          </Typography>
        </MuiBox>
        <List> 
         
          {quizIds.length > 0 ? (
            quizIds
              .filter((qId) => quizHistory[qId] && quizHistory[qId].length > 0) // Ensure only valid TF quiz IDs with attempts are shown
              .map((qId) => (
                <React.Fragment key={qId}>
                  <ListItem>
                    <ListItemText
                      primary={`Quiz ID: ${qId}`}
                      primaryTypographyProps={{ fontWeight: 600, color: "#0277bd" }}
                    />
                  </ListItem>
                  {quizHistory[qId].map((attempt, index) => (
                    <ListItem key={`${qId}-${attempt.attempt_no}`} disablePadding>
                      <ListItemButton onClick={() => handleSelectHistoryAttempt(qId, attempt)}>
                        <ListItemText
                          primary={`Attempt ${attempt.attempt_no}`}
                          secondary={
                            <>
                              <span>
                                Score: {formatPercentage(attempt.results.score_percentage)} | {attempt.results.result}
                              </span>
                              <br />
                              <span style={{ color: dark ? '#fff' : '#333' }}>
                                Date: {formatTimestamp(attempt.timestamp)}
                              </span>
                            </>
                          }
                          primaryTypographyProps={{ fontWeight: 500 }}
                          secondaryTypographyProps={{
                            color: attempt.results.result.toLowerCase() === "passed" ? "#2e7d32" : "#d32f2f",
                            component: "div",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </React.Fragment>
              ))
          ) : (
            <ListItem>
              <ListItemText primary="No True/False quiz history available" />
            </ListItem>
          )}
        </List>
        <MuiBox sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={toggleHistoryDrawer(false)}
            sx={{
              width: "100%",
              borderColor: "#0277bd",
              color: "#0277bd",
              borderRadius: "50px",
            }}
          >
            Close
          </Button>
        </MuiBox>
      </Drawer> */}

      {/* History Detail Modal */}
      {/* <Modal
        open={isHistoryModalOpen}
        onClose={handleCloseHistoryModal}
        aria-labelledby="history-modal-title"
        aria-describedby="history-modal-description"
        sx={{ zIndex: 11001 }}
      >
        <Fade in={isHistoryModalOpen}>
          <MuiBox
            className={`quiz-result ${darkMode ? "dark-mode" : "light-mode"}`}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 800 },
              bgcolor: "white",
              borderRadius: "16px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
              p: { xs: 3, sm: 4 },
              // background: "linear-gradient(145deg, #ffffff, #f0f4f8)",
              maxHeight: "80vh",
              overflowY: "auto",
              animation: "scaleIn 0.3s ease-out",
              "@keyframes scaleIn": {
                "0%": {
                  transform: "translate(-50%, -50%) scale(0.8)",
                  opacity: 0,
                },
                "100%": {
                  transform: "translate(-50%, -50%) scale(1)",
                  opacity: 1,
                },
              },
            }}
          >
            <IconButton
              aria-label="close"
              onClick={handleCloseHistoryModal}
              sx={{
                position: "sticky",
                top: 2, // Reduced to ensure it’s not clipped by padding
                right: 2, // Adjusted for right-side positioning
                float: "right", // Helps align to the right in the flow
                zIndex: 10001,
                backgroundColor: "rgba(255, 255, 255, 0.69)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.84)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <MuiBox
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 3,
                justifyContent: "center",
              }}
            >
              <TrophyIcon sx={{ fontSize: 40, color: "#0277bd", mr: 1 }} />
              <Typography
                id="history-modal-title"
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  background: "linear-gradient(45deg, #0277bd, #01579b)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Attempt Details
              </Typography>
            </MuiBox>
            {error && (
              <MuiBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#ffebee",
                  borderRadius: "8px",
                  p: 2,
                  mb: 3,
                }}
              >
                <ErrorIcon sx={{ color: "#d32f2f", mr: 1 }} />
                <Typography sx={{ color: "#d32f2f" }}>{error}</Typography>
              </MuiBox>
            )}
            {selectedHistoryAttempt ? (
              <Fade in key={animateKey} timeout={300}>
                <MuiBox
                  className={`quiz-result ${mode}-mode`}
                  sx={{
                    // bgcolor: "#f9fafb",
                    borderRadius: "12px",
                    p: 3,
                    boxShadow: darkMode ? "0 4px 12px rgba(231, 213, 213, 0.1)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <MuiBox
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    {(() => {
                      const historyResults = selectedHistoryAttempt.attempt.results;
                      let duration = "N/A";
                      if (
                        historyResults.started_at &&
                        historyResults.started_at !== "N/A" &&
                        historyResults.ended_at &&
                        historyResults.ended_at !== "N/A"
                      ) {
                        const startTime = new Date(historyResults.started_at);
                        const endTime = new Date(historyResults.ended_at);
                        const diffMs = endTime - startTime;
                        const hours = Math.floor(diffMs / (1000 * 60 * 60));
                        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
                        const parts = [];
                        if (hours > 0) parts.push(`${hours}h`);
                        if (minutes > 0) parts.push(`${minutes}m`);
                        if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
                        duration = parts.join(" ");
                      }

                      const historyFields = [
                        {
                          label: "Total Questions",
                          value: historyResults.total_questions || "N/A",
                          icon: InfoIcon,
                        },
                        {
                          label: "Attempted Questions",
                          value: `${historyResults.attempted_questions ?? 0} out of ${historyResults.total_questions || "N/A"}`,
                          icon: CheckIcon,
                        },
                        {
                          label: "Correct Answers",
                          value: historyResults.correct_answers ?? "N/A",
                          icon: CorrectIcon,
                        },
                        {
                          label: "Wrong Answers",
                          value: historyResults.wrong_answers ?? "N/A",
                          icon: IncorrectIcon,
                        },
                        {
                          label: "Final Score",
                          value: `${historyResults.final_score ?? "N/A"} out of ${historyResults.total_questions || "N/A"}`,
                          icon: TrophyIcon,
                        },
                        {
                          label: "Score Percentage",
                          value: formatPercentage(historyResults.score_percentage),
                          icon: CheckIcon,
                        },
                        {
                          label: "Result",
                          value: `${historyResults.result ?? "N/A"}${historyResults.passing_percentage ? ` (Passing: ${formatPercentage(historyResults.passing_percentage)})` : ""}`,
                          icon: CheckIcon,
                          bgcolor: historyResults.result?.toLowerCase() === "passed" ? "#e8f5e9" : historyResults.result?.toLowerCase() === "failed" ? "#ffebee" : "#f0f4f8",
                          color: historyResults.result?.toLowerCase() === "passed" ? "#2e7d32" : historyResults.result?.toLowerCase() === "failed" ? "#d32f2f" : "#01579b",
                        },
                        {
                          label: "Started At",
                          value:
                            historyResults.started_at && historyResults.started_at !== "N/A"
                              ? new Date(historyResults.started_at).toLocaleString("en-US", {
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                              : "N/A",
                          icon: TimeIcon,
                        },
                        {
                          label: "Ended At",
                          value:
                            historyResults.ended_at && historyResults.ended_at !== "N/A"
                              ? new Date(historyResults.ended_at).toLocaleString("en-US", {
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                              : "N/A",
                          icon: TimeIcon,
                        },
                        {
                          label: "Duration",
                          value: duration,
                          icon: TimeIcon,
                        },
                      ];

                      return historyFields.map((field, index) => (
                        <MuiBox
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            bgcolor: field.bgcolor || "#e3f2fd",
                            borderRadius: "8px",
                            p: 1,
                            pr: 2,
                          }}
                        >
                          <field.icon
                            sx={{
                              color: field.color || "#0277bd",
                              mr: 1,
                            }}
                          />
                          <Typography
                            sx={{
                              fontWeight:
                                field.label === "Result" ||
                                  field.label === "Final Score" ||
                                  field.label === "Score Percentage"
                                  ? 600
                                  : 500,
                              color:
                                field.color ||
                                (field.label === "Result" ||
                                  field.label === "Final Score" ||
                                  field.label === "Score Percentage"
                                  ? "#01579b"
                                  : "#333"),
                            }}
                          >
                            <strong>{field.label}:</strong> {field.value}
                          </Typography>
                        </MuiBox>
                      ));
                    })()}
                  </MuiBox>
                </MuiBox>
              </Fade>
            ) : (
              <MuiBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#f0f4f8",
                  borderRadius: "8px",
                  p: 2,
                  mb: 3,
                }}
              >
                <InfoIcon sx={{ color: "#666", mr: 1 }} />
                <Typography sx={{ color: "#666" }}>
                  No results available.
                </Typography>
              </MuiBox>
            )}
            <MuiBox sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#0277bd",
                  color: "white",
                  borderRadius: "50px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: "#01579b",
                    transform: "scale(1.05)",
                    transition: "all 0.2s",
                  },
                }}
                onClick={handleCloseHistoryModal}
              >
                Close
              </Button> */}
      {/* <Button
          variant="outlined"
          sx={{
            borderColor: "#0277bd",
            color: "#0277bd",
            borderRadius: "50px",
            px: 4,
            py: 1.5,
            fontWeight: 600,
            "&:hover": {
              borderColor: "#01579b",
              color: "#01579b",
              transform: "scale(1.05)",
              transition: "all 0.2s",
            },
          }}
          onClick={handleOpenHistoryReviewModal}
          disabled={!selectedHistoryAttempt || !selectedHistoryAttempt.attempt.answers || selectedHistoryAttempt.attempt.answers.length === 0}
        >
          Review Answers
        </Button> */}
      {/* </MuiBox>
          </MuiBox>
        </Fade>
      </Modal> */}

      {/* History Review Modal */}
      {/* <Modal
  open={isHistoryReviewModalOpen}
  aria-labelledby="history-review-modal"
  aria-describedby="history-review-details"
  sx={{ zIndex: 10000 }}
>
  <Fade in={isHistoryReviewModalOpen}>
    <MuiBox
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "90%", sm: 600 },
        bgcolor: "white",
        borderRadius: "16px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
        p: { xs: 3, sm: 4 },
        background: "linear-gradient(145deg, #ffffff, #f0f4f8)",
        maxHeight: "80vh",
        overflowY: "auto",
        animation: "scaleIn 0.3s ease-out",
        "@keyframes scaleIn": {
          "0%": {
            transform: "translate(-50%, -50%) scale(0.8)",
            opacity: 0,
          },
          "100%": {
            transform: "translate(-50%, -50%) scale(1)",
            opacity: 1,
          },
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleCloseHistoryReviewModal}
      sx={{
          position: "sticky",
          top: 2, // Reduced to ensure it’s not clipped by padding
          right: 2, // Adjusted for right-side positioning
          float: "right", // Helps align to the right in the flow
          zIndex: 10001,
          backgroundColor: "rgba(255, 255, 255, 0.69)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.84)",
          },
        }}
      >
        <CloseIcon />
      </IconButton>
      <MuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          justifyContent: "center",
        }}
      >
        <CheckIcon sx={{ fontSize: 40, color: "#0277bd", mr: 1 }} />
        <Typography
          id="history-review-modal"
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 700,
            background: "linear-gradient(45deg, #0277bd, #01579b)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Review Your Answers
        </Typography>
      </MuiBox>
      {questions.length > 0 && reviewAnswersFromHistory.length > 0 ? (
        questions.map((question, index) => {
          if (index >= reviewAnswersFromHistory.length) {
            return null;
          }
          const userAnswer = reviewAnswersFromHistory[index] || "Not answered";
          const isCorrect =
            userAnswer !== "Not answered" &&
            userAnswer.toLowerCase() === question.answer.toString().toLowerCase();
          return (
            <MuiBox
              key={question.id}
              sx={{
                bgcolor: "#f9fafb",
                borderRadius: "12px",
                p: 3,
                mb: 3,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#0277bd",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Question {index + 1}
              </Typography>
              <MuiBox sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 500, color: "#333" }}>
                  {renderContent(question.question)}
                </Typography>
              </MuiBox>
              <MuiBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  bgcolor: userAnswer === "Not answered" ? "#f0f4f8" : isCorrect ? "#e8f5e9" : "#ffebee",
                  borderRadius: "8px",
                  p: 1,
                }}
              >
                {userAnswer === "Not answered" ? (
                  <InfoIcon sx={{ color: "#666", mr: 1 }} />
                ) : isCorrect ? (
                  <CorrectIcon sx={{ color: "#2e7d32", mr: 1 }} />
                ) : (
                  <IncorrectIcon sx={{ color: "#d32f2f", mr: 1 }} />
                )}
                <Typography sx={{ fontWeight: 500, color: "#333" }}>
                  <strong>Your Answer:</strong> {userAnswer}
                </Typography>
              </MuiBox>
              <MuiBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CorrectIcon sx={{ color: "#2e7d32", mr: 1 }} />
                <Typography sx={{ fontWeight: 500, color: "#333" }}>
                  <strong>Correct Answer:</strong>{" "}
                  {renderContent(question.answer)}
                </Typography>
              </MuiBox>
              {question.explanation && (
                <MuiBox sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 500, color: "#333" }}>
                    <strong>Explanation:</strong>{" "}
                    {renderContent(question.explanation)}
                  </Typography>
                </MuiBox>
              )}
            </MuiBox>
          );
        }).filter(Boolean)
      ) : (
        <MuiBox
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "#f0f4f8",
            borderRadius: "8px",
            p: 2,
            mb: 3,
          }}
        >
          <InfoIcon sx={{ color: "#666", mr: 1 }} />
          <Typography sx={{ color: "#666" }}>
            {questions.length === 0
              ? "No questions available to review."
              : "No answers available to review for this attempt."}
          </Typography>
        </MuiBox>
      )}
      <MuiBox sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          sx={{
            bgcolor: "#0277bd",
            color: "white",
            borderRadius: "50px",
            px: 4,
            py: 1.5,
            fontWeight: 600,
            "&:hover": {
              bgcolor: "#01579b",
              transform: "scale(1.05)",
              transition: "all 0.2s",
            },
          }}
          onClick={handleCloseHistoryReviewModal}
        >
          Back to History
        </Button>
      </MuiBox>
    </MuiBox>
  </Fade>
</Modal> */}
      <div
        className={`quiz-modal ${mode}-mode`}
        style={{
          minHeight: "100vh",
          // background: "linear-gradient(to bottom right, #e0f7fa, #b2ebf2)",
          padding: "40px 20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Start Quiz Modal */}
        <Modal
          open={isStartModalOpen && shouldShowStartModal}
          aria-labelledby="start-quiz-modal"
          aria-describedby="start-quiz-settings"
          sx={{ zIndex: 9999 }}
        >
          <Fade in={isStartModalOpen && shouldShowStartModal}>
            <MuiBox
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "90%", sm: 500 },
                bgcolor: "white",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                p: { xs: 3, sm: 4 },
                background: mode === "dark" ? "linear-gradient(145deg, #163642ff, #373d52ff)" : mode === "custom" ? customColors.background || "linear-gradient(145deg, #ffffff, #f0f4f8)" : "linear-gradient(145deg, #ffffff, #f0f4f8)",
                animation: "scaleIn 0.3s ease-out",
                "@keyframes scaleIn": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(0.8)",
                    opacity: 0,
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(1)",
                    opacity: 1,
                  },
                },
              }}
            >
              <MuiBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  justifyContent: "center",
                }}
              >
                <CheckIcon sx={{ fontSize: 40, color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd", mr: 1 }} />
                <Typography
                  id="start-quiz-modal"
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    background: mode === "dark" ? "linear-gradient(45deg, #0277bd, #01579b)" : mode === "custom" ? customColors.text : "linear-gradient(45deg, #0277bd, #01579b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Quiz Settings
                </Typography>
              </MuiBox>
              <MuiBox sx={{ mb: 3 }}>
                <MuiBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Max Attempts"
                    type="number"
                    value={maxAttempts}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string or numbers >= 1
                      if (value === "" || (Number(value) >= 1 && Number.isInteger(Number(value)))) {
                        setMaxAttempts(value);
                      }
                    }}
                    InputLabelProps={{
                      style: {
                        color:
                          mode === "dark"
                            ? "#ffffff" // dark mode
                            : mode === "custom"
                              ? customColors.text // custom theme
                              : "#000000", // light mode
                      },
                    }}
                    placeholder="Leave blank for unlimited"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: "#b0bec5",
                        },
                        "&:hover fieldset": {
                          borderColor: "#0277bd",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#0277bd",
                        },
                      },
                    }}
                  />
                  <Tooltip
                    title="Set the maximum number of attempts allowed for the quiz. Leave blank for unlimited attempts."
                    placement="right"
                    arrow
                    PopperProps={{
                      style: { zIndex: 10000 },
                    }}
                    sx={{
                      "& .MuiTooltip-tooltip": {
                        fontSize: "0.9rem",
                        bgcolor: "#0277bd",
                        color: "white",
                        borderRadius: "8px",
                        p: 1.5,
                      },
                      "& .MuiTooltip-arrow": {
                        color: "#0277bd",
                      },
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginLeft: "8px", cursor: "pointer" }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v.01" />
                      <path d="M12 8v4" />
                    </svg>
                  </Tooltip>
                </MuiBox>
                <MuiBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Passing Percentage"
                    type="number"
                    value={passingPercentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string or numbers >= 1 and <= 100
                      if (value === "" || (Number(value) >= 1 && Number(value) <= 100)) {
                        setPassingPercentage(value);
                      }
                    }}
                    InputLabelProps={{
                      style: {
                        color:
                          mode === "dark"
                            ? "#ffffff" // dark mode
                            : mode === "custom"
                              ? customColors.text // custom theme
                              : "#000000", // light mode
                      },
                    }}
                    placeholder="Enter percentage (defaults to 35)"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: "#b0bec5",
                        },
                        "&:hover fieldset": {
                          borderColor: "#0277bd",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#0277bd",
                        },
                      },
                    }}
                  />
                  <Tooltip
                    title="Set the minimum percentage required to pass the quiz (1-100). Defaults to 35% if not specified."
                    placement="right"
                    arrow
                    PopperProps={{
                      style: { zIndex: 10000 },
                    }}
                    sx={{
                      "& .MuiTooltip-tooltip": {
                        fontSize: "0.9rem",
                        bgcolor: "#0277bd",
                        color: "white",
                        borderRadius: "8px",
                        p: 1.5,
                      },
                      "& .MuiTooltip-arrow": {
                        color: "#0277bd",
                      },
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginLeft: "8px", cursor: "pointer" }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v.01" />
                      <path d="M12 8v4" />
                    </svg>
                  </Tooltip>
                </MuiBox>
                <MuiBox sx={{ display: "flex", alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                  <FormControl component="fieldset" sx={{ flex: "0 0 auto", mr: 4 }}>
                    <MuiBox sx={{ display: "flex", alignItems: "center" }}>
                      <FormLabel component="legend" sx={{ color: mode === "dark" ? "white" : mode === "custom" ? customColors.text : negativeMarking ? "#fff" : "#333", mb: 1 }}>
                        Negative Marking
                      </FormLabel>
                      <Tooltip
                        title="Enable negative marking to deduct points for incorrect answers. If disabled, incorrect answers do not affect the score."
                        placement="left"
                        arrow
                        PopperProps={{
                          style: { zIndex: 10000 },
                        }}
                        sx={{
                          "& .MuiTooltip-tooltip": {
                            fontSize: "0.9rem",
                            bgcolor: "#0277bd",
                            color: "white",
                            borderRadius: "8px",
                            p: 1.5,
                          },
                          "& .MuiTooltip-arrow": {
                            color: "#0277bd",
                          },
                        }}
                      >
                        <svg
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd"}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginLeft: "8px", marginBottom: "4px", cursor: "pointer", }}
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v.01" />
                          <path d="M12 8v4" />
                        </svg>
                      </Tooltip>
                    </MuiBox>
                    <RadioGroup
                      row
                      value={negativeMarking ? "yes" : "no"}
                      onChange={(e) => setNegativeMarking(e.target.value === "yes")}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio sx={{ color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd" }} />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio sx={{ color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd" }} />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                  <FormControl component="fieldset" sx={{ flex: "0 0 auto", marginLeft: 2 }}>
                    <MuiBox sx={{ display: "flex", alignItems: "center" }}>
                      <FormLabel component="legend" sx={{ color: mode === "dark" ? "white" : mode === "custom" ? customColors.text : negativeMarking ? "#fff" : "#333", mb: 1 }}>
                        Enable Timer
                      </FormLabel>
                      <Tooltip
                        title="Enable the timer to set a time limit for the quiz (30 seconds per question). Disable to take the quiz without a time constraint."
                        placement="right"
                        arrow
                        PopperProps={{
                          style: { zIndex: 10000 },
                        }}
                        sx={{
                          "& .MuiTooltip-tooltip": {
                            fontSize: "0.9rem",
                            bgcolor: "#0277bd",
                            color: "white",
                            borderRadius: "8px",
                            p: 1.5,
                          },
                          "& .MuiTooltip-arrow": {
                            color: "#0277bd",
                          },
                        }}
                      >
                        <svg
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke={mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd"}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginLeft: "8px", marginBottom: "4px", cursor: "pointer", }}
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v.01" />
                          <path d="M12 8v4" />
                        </svg>
                      </Tooltip>
                    </MuiBox>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isTimerEnabled}
                          onChange={(e) => setIsTimerEnabled(e.target.checked)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.bgColor : "#0277bd",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.bgColor : "#0277bd",
                            },
                          }}
                        />
                      }
                      label={isTimerEnabled ? "Timer Enabled" : "Timer Disabled"}
                    />
                  </FormControl>
                </MuiBox>
              </MuiBox>
              <MuiBox sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#0277bd",
                    color: "white",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: "#01579b",
                      transform: "scale(1.05)",
                      transition: "all 0.2s",
                    },
                  }}
                  onClick={handleStartQuiz}
                >
                  Start Quiz
                </Button>
              </MuiBox>
            </MuiBox>
          </Fade>
        </Modal>

        {/* Results Modal */}
        <Modal
          open={isResultModalOpen}
          aria-labelledby="result-modal"
          aria-describedby="result-details"
          sx={{
            zIndex: 1300,
            position: "fixed", // Ensure the modal is isolated
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Fade in={isResultModalOpen}>
            <Box
              className={`quiz-result ${mode}-mode`}
              ref={modalRef}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "90%", sm: 800 },
                bgcolor: "white",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                p: { xs: 3, sm: 4 },
                // background: "linear-gradient(145deg, #ffffff, #f0f4f8)",
                maxHeight: "80vh",
                overflowY: "auto",
                animation: "scaleIn 0.3s ease-out",
                "@keyframes scaleIn": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(0.8)",
                    opacity: 0,
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(1)",
                    opacity: 1,
                  },
                },
              }}
            >
              <IconButton
                aria-label="close"
                onClick={handleCloseResultModal}
                sx={{
                  position: "sticky",
                  top: 2, // Reduced to ensure it’s not clipped by padding
                  right: 2, // Adjusted for right-side positioning
                  float: "right", // Helps align to the right in the flow
                  zIndex: 10001,
                  backgroundColor: "rgba(255, 255, 255, 0.69)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.84)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  justifyContent: "center",
                }}
              >
                <TrophyIcon sx={{ fontSize: 40, color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd", mr: 1 }} />
                <Typography
                  id="result-modal"
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    background: mode === "dark" ? "linear-gradient(45deg, #0277bd, #56aef1ff)" : mode === "custom" ? customColors.text : "linear-gradient(45deg, #0277bd, #56aef1ff)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Quiz Results
                </Typography>
              </Box>
              {error && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#ffebee",
                    borderRadius: "8px",
                    p: 2,
                    mb: 3,
                  }}
                >
                  <ErrorIcon sx={{ color: "#d32f2f", mr: 1 }} />
                  <Typography sx={{ color: "#d32f2f" }}>{error}</Typography>
                </Box>
              )}
              {results ? (
                <Fade in key={animateKey} timeout={300}>
                  <Box
                    className={`quiz-result-box ${mode}-mode`}
                    sx={{
                      // bgcolor: "#f9fafb",
                      borderRadius: "12px",
                      p: 3,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <Typography sx={{ fontWeight: 500, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text || "#333" : "#333", mb: 1 }}>
                        Select Attempt
                      </Typography>
                      <Select
                        value={selectedAttempt ?? ""}
                        onChange={handleAttemptChange}
                        size="small"
                        MenuProps={{
                          disablePortal: true,
                          anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                          },
                          transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                          },
                          PopperProps: {
                            placement: "bottom-start",
                            modifiers: [
                              {
                                name: "offset",
                                options: {
                                  offset: [0, 4],
                                },
                              },
                              {
                                name: "preventOverflow",
                                options: {
                                  boundariesElement: "viewport",
                                },
                              },
                            ],
                          },
                          PaperProps: {
                            sx: {
                              zIndex: 10001,
                              maxHeight: 200,
                              width: 200,
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                              "& .MuiMenuItem-root": {
                                fontSize: "0.875rem",
                                padding: "6px 12px",
                                "&:hover": {
                                  backgroundColor: "#e3f2fd",
                                },
                              },
                            },
                          },
                        }}
                        sx={{
                          borderRadius: "8px",
                          maxWidth: 200,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#b0bec5",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#0277bd",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#0277bd",
                          },
                          "& .MuiSelect-select": {
                            padding: "6px 26px 6px 12px",
                            fontSize: "0.875rem",
                          },
                        }}
                      >
                        {attempts.length > 0 ? (
                          attempts.map((attempt) => (
                            <MenuItem key={attempt.attempt_no} value={attempt.attempt_no}>
                              Attempt {attempt.attempt_no}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem value="" disabled>
                            No attempts available
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 2,
                      }}
                    >
                      {resultFields
                        .slice(
                          (currentPage - 1) * itemsPerPage,
                          currentPage * itemsPerPage
                        )
                        .map((field, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                              bgcolor: field.bgcolor || "#e3f2fd",
                              borderRadius: "8px",
                              p: 1,
                              pr: 2,
                            }}
                          >
                            <field.icon
                              sx={{
                                color: field.color || "#0277bd",
                                mr: 1,
                              }}
                            />
                            <Typography
                              sx={{
                                fontWeight:
                                  field.label === "Result" ||
                                    field.label === "Final Score" ||
                                    field.label === "Score Percentage"
                                    ? 600
                                    : 500,
                                color:
                                  field.color ||
                                  (field.label === "Result" ||
                                    field.label === "Final Score" ||
                                    field.label === "Score Percentage"
                                    ? "#01579b"
                                    : "#333"),
                              }}
                            >
                              <strong>{field.label}:</strong> {field.value}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                    {totalPages > 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 3,
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            borderColor: "#0277bd",
                            color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd",
                            borderRadius: "50px",
                            px: 3,
                            py: 1,
                            "&:hover": {
                              borderColor: "#01579b",
                              color: "#01579b",
                            },
                            "&.Mui-disabled": {
                              borderColor: "#b0bec5",
                              color: "#b0bec5",
                            },
                          }}
                          onClick={handlePreviousPage}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </Button>
                        <Typography sx={{ fontWeight: 500 }}>
                          Page {currentPage} of {totalPages}
                        </Typography>
                        <Button
                          variant="contained"
                          sx={{
                            borderColor: "#0277bd",
                            color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#e4eaeeff",
                            borderRadius: "50px",
                            px: 3,
                            py: 1,
                            "&:hover": {
                              borderColor: "#01579b",
                              color:"#e4eaeeff",
                            },
                            "&.Mui-disabled": {
                              borderColor: "#b0bec5",
                              color: "#b0bec5",
                            },
                          }}
                          onClick={handleNextPage}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Fade>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#f0f4f8",
                    borderRadius: "8px",
                    p: 2,
                    mb: 3,
                  }}
                >
                  <InfoIcon sx={{ color: "#666", mr: 1 }} />
                  <Typography sx={{ color: "#666" }}>
                    No results available.
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#0277bd",
                    color: "white",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: "#01579b",
                      transform: "scale(1.05)",
                      transition: "all 0.2s",
                    },
                  }}
                  onClick={handleCloseResultModal}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    borderColor: "#0277bd",
                    color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#e4eaeeff",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#01579b",
                      color: "#e4eaeeff",
                      transform: "scale(1.05)",
                      transition: "all 0.2s",
                    },
                  }}
                  onClick={handleOpenReviewModal}
                >
                  Review Answers
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Non-Editable Review Modal (Post-Quiz) */}
        <Modal
          open={isReviewModalOpen}
          aria-labelledby="review-modal"
          aria-describedby="review-details"
          sx={{ zIndex: 10000 }}
        >
          <Fade in={isReviewModalOpen}>
            <Box
              className={`quiz-result ${mode}-mode`}
              ref={reviewModalRef}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "90%", sm: 600 },
                bgcolor: "white",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                p: { xs: 3, sm: 4 },
                // background: "linear-gradient(145deg, #ffffff, #f0f4f8)",
                maxHeight: "80vh",
                overflowY: "auto",
                animation: "scaleIn 0.3s ease-out",
                "@keyframes scaleIn": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(0.8)",
                    opacity: 0,
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(1)",
                    opacity: 1,
                  },
                },
              }}
            >
              <IconButton
                aria-label="close"
                onClick={handleCloseReviewModal}
                sx={{
                  position: "sticky",
                  top: 2, // Reduced to ensure it’s not clipped by padding
                  right: 2, // Adjusted for right-side positioning
                  float: "right", // Helps align to the right in the flow
                  zIndex: 10001,
                  backgroundColor: "rgba(255, 255, 255, 0.69)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.84)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  justifyContent: "center",
                }}
              >
                <CheckIcon sx={{ fontSize: 40, color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd", mr: 1 }} />
                <Typography
                  id="review-modal"
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    background: mode === "dark" ? "linear-gradient(45deg, #0277bd, #01579b)" : mode === "custom" ? customColors.text : "linear-gradient(45deg, #0277bd, #01579b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Review Your Answers
                </Typography>
              </Box>
              {questions.length > 0 ? (
                questions.map((question, index) => {
                  const userAnswer = allAnswers[index] !== null ? allAnswers[index] : "Not answered";
                  const correctAnswer = question.answer.toString();
                  const isCorrect = userAnswer.toLowerCase() === correctAnswer.toLowerCase();
                  console.log("Review check:", {
                    questionId: question.id,
                    userAnswer,
                    correctAnswer,
                    isCorrect,
                  });
                  return (
                    <Box
                      className={`quiz-result-box ${mode}-mode`}
                      key={question.id}
                      sx={{
                        // bgcolor: "#f9fafb",
                        borderRadius: "12px",
                        p: 3,
                        mb: 3,
                        boxShadow: mode === "dark" ? "0 4px 12px rgba(233, 215, 215, 0.1)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd",
                          mb: 2,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Question {index + 1}
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: 500, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text || "#333" : "#333" }}>
                          {renderContent(question.question)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                          bgcolor: isCorrect ? "#e8f5e9" : "#ffebee",
                          borderRadius: "8px",
                          p: 1,
                        }}
                      >
                        {isCorrect ? (
                          <CorrectIcon sx={{ color: "#2e7d32", mr: 1 }} />
                        ) : (
                          <IncorrectIcon sx={{ color: "#d32f2f", mr: 1 }} />
                        )}
                        <Typography sx={{ fontWeight: 500, color: "#333" }}>
                          <strong>Your Answer:</strong> {userAnswer}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <CorrectIcon sx={{ color: "#2e7d32", mr: 1 }} />
                        <Typography sx={{ fontWeight: 500, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text || "#333" : "#333" }}>
                          <strong>Correct Answer:</strong> {correctAnswer}
                        </Typography>
                      </Box>
                      {question.explanation && (
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontWeight: 500, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text || "#333" : "#333" }}>
                            <strong>Explanation:</strong>{" "}
                            {renderContent(question.explanation)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#f0f4f8",
                    borderRadius: "8px",
                    p: 2,
                    mb: 3,
                  }}
                >
                  <InfoIcon sx={{ color: "#666", mr: 1 }} />
                  <Typography sx={{ color: "#666" }}>
                    No answers available to review.
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#0277bd",
                    color: "white",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: "#01579b",
                      transform: "scale(1.05)",
                      transition: "transform 0.2s",
                    },
                  }}
                  onClick={handleCloseReviewModal}
                >
                  Back to Results
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Editable Review Modal (Pre-Quiz Submission) */}
        <Modal
          open={isEditableReviewModalOpen}
          aria-labelledby="editable-review-modal"
          aria-describedby="editable-review-details"
          sx={{ zIndex: 10000 }}
        >
          <Fade in={isEditableReviewModalOpen}>
            <Box
              className={`quiz-result ${mode}-mode`}
              ref={editableReviewModalRef}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "90%", sm: 600 },
                bgcolor: "white",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                p: { xs: 3, sm: 4 },
                // background: "linear-gradient(145deg, #ffffff, #f0f4f8)",
                maxHeight: "80vh",
                overflowY: "auto",
                animation: "scaleIn 0.3s ease-out",
                "@keyframes scaleIn": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(0.8)",
                    opacity: 0,
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(1)",
                    opacity: 1,
                  },
                },
              }}
            >
              <IconButton
                aria-label="close"
                onClick={handleCloseEditableReviewModal}
                sx={{
                  position: "sticky",
                  top: 2, // Reduced to ensure it’s not clipped by padding
                  right: 2, // Adjusted for right-side positioning
                  float: "right", // Helps align to the right in the flow
                  zIndex: 10001,
                  backgroundColor: "rgba(255, 255, 255, 0.69)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.84)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  justifyContent: "center",
                }}
              >
                <CheckIcon sx={{ fontSize: 40, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#0277bd", mr: 1 }} />
                <Typography
                  id="editable-review-modal"
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    background: mode === "dark" ? "linear-gradient(45deg, #0277bd, #01579b)" : mode === "custom" ? customColors.text : "linear-gradient(45deg, #0277bd, #01579b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Review and Edit Your Answers
                </Typography>
              </Box>
              {questions.length > 0 ? (
                questions.map((question, index) => (
                  <Box
                    className={`quiz-result-box ${mode}-mode`}
                    key={question.id}
                    sx={{
                      // bgcolor: "#f9fafb",
                      borderRadius: "12px",
                      p: 3,
                      mb: 3,
                      boxShadow: mode === "dark" ? "0 4px 12px rgba(250, 243, 243, 0.1)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#0277bd",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      Question {index + 1}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 500, color: mode === "dark" ? "#ffffff" : mode === "custom" ? customColors.text || "#333" : "#333" }}>
                        {renderContent(question.question)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 500, color: mode === "dark" ? "#ffffff" : mode === "custom" ? customColors.text || "#333" : "#333", mb: 1 }}>
                        <strong>Your Answer:</strong>
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                          variant={allAnswers[index] === "True" ? "contained" : "outlined"}
                          // color={darkMode ? "#E0E0E0" : "primary"}
                          onClick={() => handleAnswerEdit(index, "True")}
                          sx={{
                            flex: 1,
                            bgcolor: mode === "dark"
                              ? "#E0E0E0"
                              : mode === "custom" ? customColors.primary || "#1976d2" : "primary.main",
                            color: mode === "dark" ? "#000" : mode === "custom" ? customColors.text || "#fff" : "#fff",
                            "&:hover": {
                              bgcolor: mode === "dark" ? "#bdbdbd" : "primary.dark",
                            },
                          }}
                        >
                          True
                        </Button>
                        <Button
                          variant={allAnswers[index] === "False" ? "contained" : "outlined"}
                          // color={darkMode ? "#E0E0E0" : "primary"}
                          onClick={() => handleAnswerEdit(index, "False")}
                          sx={{
                            flex: 1,
                            bgcolor: mode === "dark"
                              ? "#E0E0E0"
                              : mode === "custom" ? customColors.primary || "#1976d2" : "primary.main",
                            color: mode === "dark" ? "#000" : mode === "custom" ? customColors.text || "#fff" : "#fff",
                            "&:hover": {
                              bgcolor: mode === "dark" ? "#bdbdbd" : "primary.dark",
                            },
                          }}
                        >
                          False
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#f0f4f8",
                    borderRadius: "8px",
                    p: 2,
                    mb: 3,
                  }}
                >
                  <InfoIcon sx={{ color: "#666", mr: 1 }} />
                  <Typography sx={{ color: "#666" }}>
                    No answers available to review.
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#0277bd",
                    color: "white",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: "#01579b",
                      transform: "scale(1.05)",
                      transition: "transform 0.2s",
                    },
                  }}
                  onClick={handleSaveAndFinish}
                >
                  Save and Finish
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    borderColor: "#0277bd",
                    color: "#0277bd",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#01579b",
                      color: "#01579b",
                      transform: "scale(1.05)",
                      transition: "transform 0.2s",
                    },
                  }}
                  onClick={handleCloseEditableReviewModal}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Time-Up Modal */}
        <Modal
          open={isTimeUpModalOpen}
          aria-labelledby="time-up-modal"
          aria-describedby="time-up-message"
          sx={{ zIndex: 9999 }}
        >
          <Fade in={isTimeUpModalOpen}>
            <Box
              className={`quiz-modal ${mode}-mode`}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "90%", sm: 400 },
                bgcolor: "white",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                p: { xs: 3, sm: 4 },
                // background: "linear-gradient(145deg, #ffffff, #f0f4f8)",
                animation: "scaleIn 0.3s ease-out",
                "@keyframes scaleIn": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(0.8)",
                    opacity: 0,
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(1)",
                    opacity: 1,
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 3,
                  justifyContent: "center",
                }}
              >
                <ErrorIcon sx={{ fontSize: 40, color: "#d32f2f", mr: 1 }} />
                <Typography
                  id="time-up-modal"
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    background: "linear-gradient(45deg, #d32f2f, #b71c1c)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Time's Up!
                </Typography>
              </Box>
              <Typography sx={{ mb: 3, color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#333", textAlign: "center" }}>
                The quiz time has expired. Your answers have been submitted.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#0277bd",
                    color: "white",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: "#01579b",
                      transform: "scale(1.05)",
                      transition: "all 0.2s",
                    },
                  }}
                  onClick={handleCloseModal}
                >
                  View Results
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Close Confirmation Dialog */}
        <Dialog
          open={isCloseDialogOpen}
          onClose={handleCancelClose}
          aria-labelledby="close-dialog-title"
          PaperProps={{
            sx: {
              borderRadius: "16px",
            },
          }}
        >
          <DialogTitle id="close-dialog-title" sx={{ bgcolor: mode === "dark" ? "#333" : mode === "custom" ? customColors.background : "#fff" }} >Confirm Close</DialogTitle>
          <DialogContent sx={{ bgcolor: mode === "dark" ? "#333" : mode === "custom" ? customColors.background : "#fff" }}>
            <Typography>
              Are you sure you want to close the quiz? Your progress will be lost.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ bgcolor: mode === "dark" ? "#333" : mode === "custom" ? customColors.background : "#fff" }}>
            <Button onClick={handleCancelClose} variant="contained" color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmClose} variant="contained" color="primary" autoFocus>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading State */}
        {/* Quiz UI */}
        {!quizStarted && !isQuizFinished ? (
          <div
            style={{
              backgroundColor: mode === "dark" ? "#2c2c2c" : mode === "custom" ? customColors.background : "#ffffff",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            Please configure quiz settings to start.
          </div>
        ) : !questions.length && !isQuizFinished ? (
          <div
            style={{
              backgroundColor: mode === "dark" ? "#2c2c2c" : mode === "custom" ? customColors.background : "#ffffff",
              padding: "20px",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            Loading questions...
          </div>
        ) : isQuizFinished ? (
          <div
            className={`quiz-result ${mode}-mode`}
            style={{
              position: "relative", // Isolate the container
              overflow: "hidden", // Prevent overflow from affecting layout
              maxHeight: "500px", // Constrain the height (adjust as needed)
              // backgroundColor: "#ffffff",
              padding: "40px",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
              maxWidth: "800px",
              width: "100%",
            }}
          >
            <MuiBox
              sx={{ display: "flex", alignItems: "center", mb: 3, justifyContent: "center" }}
            >
              <TrophyIcon sx={{ fontSize: 40, color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd", mr: 1 }} />
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 700,
                  background: mode === "dark" ? "linear-gradient(45deg, #0277bd, #01579b)" : mode === "custom" ? customColors.text : "linear-gradient(45deg, #0277bd, #01579b)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                True/False Quiz Results
              </Typography>
            </MuiBox>
            {error ? (
              <MuiBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#ffebee",
                  borderRadius: "8px",
                  p: 2,
                  mb: 3,
                }}
              >
                <ErrorIcon sx={{ color: "#d32f2f", mr: 1 }} />
                <Typography sx={{ color: "#d32f2f" }}>{error}</Typography>
              </MuiBox>
            ) : results ? (
              <MuiBox
                className={`quiz-result-box ${mode}-mode`}
                sx={{
                  // bgcolor: "#f9fafb",
                  borderRadius: "12px",
                  p: 3,
                  boxShadow: mode === "dark" ? "0 4px 12px rgba(233, 215, 215, 0.1)" : "0 4px 12px rgba(0, 0, 0, 0.1)",
                }}
              >
                <MuiBox
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  {resultFields.map((field, index) => (
                    <MuiBox
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                        bgcolor: field.bgcolor || "#e3f2fd",
                        borderRadius: "8px",
                        p: 1,
                        pr: 2,
                      }}
                    >
                      <field.icon
                        sx={{
                          color: field.color || "#0277bd",
                          mr: 1,
                        }}
                      />
                      <Typography
                        sx={{
                          fontWeight:
                            field.label === "Result" ||
                              field.label === "Final Score" ||
                              field.label === "Score Percentage"
                              ? 600
                              : 500,
                          color:
                            field.color ||
                            (field.label === "Result" ||
                              field.label === "Final Score" ||
                              field.label === "Score Percentage"
                              ? "#01579b"
                              : "#333"),
                        }}
                      >
                        <strong>{field.label}:</strong> {field.value}
                      </Typography>
                    </MuiBox>
                  ))}
                </MuiBox>
              </MuiBox>
            ) : (
              <MuiBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#f0f4f8",
                  borderRadius: "8px",
                  p: 2,
                  mb: 3,
                }}
              >
                <InfoIcon sx={{ color: "#666", mr: 1 }} />
                <Typography sx={{ color: "#666" }}>
                  No results available.
                </Typography>
              </MuiBox>
            )}
            <MuiBox sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
              <Button
                variant="outlined"
                sx={{
                  bgcolor: "#0277bd",
                  color: "white",
                  borderRadius: "50px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: "#01579b",
                    transform: "scale(1.05)",
                    transition: "all 0.2s",
                  },
                }}
                onClick={handleRetryQuiz}
                disabled={
                  quizMaxAttempts !== null && attemptCount >= quizMaxAttempts
                }
              >
                Retry Quiz
              </Button>
              <Button
                variant="contained"
                sx={{
                  borderColor: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd",
                  color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#e4eaeeff",
                  borderRadius: "50px",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#01579b",
                    color: "#e4eaeeff",
                    transform: "scale(1.05)",
                    transition: "all 0.2s",
                  },
                }}
                onClick={handleViewResult}
              >
                View Detailed Results
              </Button>
            </MuiBox>
          </div>
        ) : (
          /* Updated Quiz UI */
          <div
            style={{
              background: mode === "dark" ? "linear-gradient(145deg, #163642ff, #373d52ff)" : mode === "custom" ? customColors.background : "linear-gradient(145deg, #ffffff, #f0f4f8)",
              borderRadius: "16px",
              boxShadow: "0 6px 18px rgba(0, 0, 0, 0.1)",
              padding: "40px",
              maxWidth: "900px",
              width: "100%",
            }}
          >
            <MuiBox
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd" }}
              >
                Question {currentQuestionIndex + 1} of {questions.length}
              </Typography>
              <MuiBox sx={{ display: "flex", alignItems: "center" }}>
                {isTimerEnabled && timeLeft !== null ? (
                  <>
                    <TimeIcon sx={{ color: mode === "dark" ? "#0277bd" : mode === "custom" ? customColors.text : "#0277bd", mr: 1 }} />
                    <Typography
                      sx={{
                        fontWeight: 500,
                        color: timeLeft <= 30 ? "#d32f2f" : mode === "dark" ? "#f5eeeeff" : mode === "custom" ? customColors.text : "#333",
                      }}
                    >
                      Time Left: {formatTime(timeLeft)}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontWeight: 500, color: "#333" }}>
                    Timer Disabled
                  </Typography>
                )}
              </MuiBox>
            </MuiBox>
            <MuiBox
              sx={{
                textAlign: "center",
                background: mode === "dark" ? "linear-gradient(145deg, #163642ff, #373d52ff)" : mode === "custom" ? customColors.background : "linear-gradient(145deg, #ffffff, #f0f4f8)",
                borderRadius: "12px",
                p: 3,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              }}
            >
              <MuiBox
                sx={{
                  background: mode === "dark" ? "linear-gradient(145deg, #4b4b4bff, #373d52ff)" : mode === "custom" ? customColors.background : "linear-gradient(145deg, #ffffff, #f0f4f8)",
                  borderRadius: "12px",
                  p: 3,
                  mb: 3,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: mode === "dark" ? "#ffffff" : mode === "custom" ? customColors.text : "#333",
                    mb: 2,
                  }}
                >
                  {renderContent(questions[currentQuestionIndex].question)}
                </Typography>
                <MuiBox sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Button
                    variant={userAnswer === "True" ? "contained" : "outlined"}
                    color="secondary"
                    onClick={() => handleSelectAnswer(true)}
                    sx={{
                      flex: 1,
                      py: 1.5,
                      borderRadius: "8px",
                      fontWeight: 500,
                    }}
                  >
                    True
                  </Button>
                  <Button
                    variant={userAnswer === "False" ? "contained" : "outlined"}
                    color="secondary"
                    onClick={() => handleSelectAnswer(false)}
                    sx={{
                      flex: 1,
                      py: 1.5,
                      borderRadius: "8px",
                      fontWeight: 500,
                    }}
                  >
                    False
                  </Button>
                </MuiBox>
              </MuiBox>
              <MuiBox
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                <MuiBox sx={{ display: "flex", gap: 2 }}>
                  <IconButton
                    sx={{
                      border: mode === "dark" ? "1px solid #0277bd" : mode === "custom" ? `1px solid ${customColors.text}` : "1px solid #0277bd",
                      color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#0277bd",
                      borderRadius: "50px",
                      p: 1.5,
                      "&:hover": {
                        borderColor: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#01579b",
                        color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#01579b",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#b0bec5",
                        color: "#b0bec5",
                      },
                    }}
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestionIndex === 0}
                  >
                    <ArrowBack />
                  </IconButton>
                  <IconButton
                    sx={{
                      border: mode === "dark" ? "1px solid #0277bd" : mode === "custom" ? `1px solid ${customColors.text}` : "1px solid #0277bd",
                      color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#0277bd",
                      borderRadius: "50px",
                      p: 1.5,
                      "&:hover": {
                        borderColor: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#01579b",
                        color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#01579b",
                        backgroundColor: "#e3f2fd",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#b0bec5",
                        color: "#b0bec5",
                        backgroundColor: "#f5f5f5",
                        opacity: 0.5,
                      },
                    }}
                    onClick={handleNextQuestion}
                  // disabled={userAnswer === null || currentQuestionIndex === questions.length - 1}
                  >
                    <ArrowForward />
                  </IconButton>
                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#0277bd",
                      color: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#0277bd",
                      borderRadius: "50px",
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: mode === "dark" ? "#fff" : mode === "custom" ? customColors.text : "#01579b",
                        color: mode === "dark" ? "#01579b" : mode === "custom" ? customColors.text : "#01579b",
                      },
                      "&.Mui-disabled": {
                        borderColor: "#b0bec5",
                        color: "#b0bec5",
                      },
                    }}
                    onClick={handleSkipQuestion}
                  >
                    Skip
                  </Button>
                </MuiBox>
                <MuiBox sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: "#0277bd",
                      color: "white",
                      borderRadius: "50px",
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: "#01579b",
                        transform: "scale(1.05)",
                        transition: "all 0.2s",
                      },
                    }}
                    onClick={handleReviewAnswers}
                  >
                    Review Answers
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: "#2e7d32",
                      color: "white",
                      borderRadius: "50px",
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: "#1b5e20",
                        transform: "scale(1.05)",
                        transition: "all 0.2s",
                      },
                    }}
                    onClick={handleFinishQuiz}
                  >
                    Finish Quiz
                  </Button>
                </MuiBox>
              </MuiBox>
            </MuiBox>
          </div>
        )}
      </div>
      {/* // Updated Offline Modal (modified to guide user to retry "Finish Quiz" when online) */}
      <Modal
        open={isOfflineModalOpen}
        onClose={() => setIsOfflineModalOpen(false)} // Close modal on outside click
        aria-labelledby="offline-modal"
        aria-describedby="offline-message"
        sx={{ zIndex: 9999 }}
      >
        <Fade in={isOfflineModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: 400 },
              bgcolor: 'white',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              p: { xs: 3, sm: 4 },
              background: 'linear-gradient(145deg, #ffffff, #f0f4f8)',
              animation: 'scaleIn 0.3s ease-out',
              '@keyframes scaleIn': {
                '0%': {
                  transform: 'translate(-50%, -50%) scale(0.8)',
                  opacity: 0,
                },
                '100%': {
                  transform: 'translate(-50%, -50%) scale(1)',
                  opacity: 1,
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 3,
                justifyContent: 'center',
              }}
            >
              <ErrorIcon sx={{ fontSize: 30, color: '#d32f2f', mr: 1 }} />
              <Typography
                id="offline-modal"
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 650,
                  background: 'linear-gradient(45deg, #d32f2f, #b71c1c)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                No Internet Connection
              </Typography>
            </Box>
            <Typography sx={{ mb: 3, color: '#333', textAlign: 'center' }}>
              No internet connection. Your answers have been saved and will be submitted when you're back online.
            </Typography>
            {/* <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          sx={{
            bgcolor: '#0277bd',
            color: 'white',
            borderRadius: '50px',
            px: 4,
            py: 1.5,
            fontWeight: 600,
            '&:hover': {
              bgcolor: '#01579b',
              transform: 'scale(1.05)',
              transition: 'all 0.2s',
            },
          }}
          onClick={() => setIsOfflineModalOpen(false)}
        >
          OK
        </Button>
      </Box> */}
          </Box>
        </Fade>
      </Modal>
      <Dialog
        open={openConfirm}
        maxWidth="sm"
        fullWidth
        onClose={() => setOpenConfirm(false)}
      >
        <DialogTitle
          sx={{
            backgroundColor:
              mode === "dark"
                ? "#424242"
                : mode === "custom"
                  ? customColors.bgNav || "#ffffff"
                  : "#ffffff",
          }}
        >
          {pendingUnansweredCount > 0 ? "Unanswered Questions" : "Finish Quiz?"}
        </DialogTitle>

        {/* Close Icon */}
        <IconButton
          aria-label="close"
          onClick={() => setOpenConfirm(false)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color:
              mode === "dark"
                ? "#f1f1f1"
                : mode === "custom"
                  ? customColors.text || "#333"
                  : "#333",
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          sx={{
            backgroundColor:
              mode === "dark"
                ? "#424242"
                : mode === "custom"
                  ? customColors.bgNav || "#ffffff"
                  : "#ffffff",
          }}
        >
          {pendingUnansweredCount > 0 ? (
            <Typography>
              You still have{" "}
              <strong>{pendingUnansweredCount}</strong> unanswered question
              {pendingUnansweredCount > 1 ? "s" : ""}. Do you want to submit anyway?
            </Typography>
          ) : (
            <Typography>Are you sure you want to finish the quiz?</Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            backgroundColor:
              mode === "dark"
                ? "#424242"
                : mode === "custom"
                  ? customColors.bgNav || "#ffffff"
                  : "#ffffff",
          }}
        >
          <Button
            onClick={() => setOpenConfirm(false)}
            variant="outlined"
            sx={{
              color:
                mode === "dark"
                  ? "#44a6e9"
                  : mode === "custom"
                    ? customColors.text || "#0277bd"
                    : "#0277bd",
            }}
          >
            No, Go Back
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            color="primary"
            variant="contained"
          >
            Yes, Submit
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default TFQuiz;